<template>
  <section class="main">
    <loader v-if="loading" />
    <h1 v-if="!loading" class="intro">
      <span class="intro__inner">{{ $t("CURRENT_IPA_CACHE_STATUS") }}</span>
    </h1>
    <div v-if="!loading" class="row">
      <div class="col col_1">
        <p class="text g-text">
          {{ $t("IPA_CACHE_DESC_1") }}
        </p>
        <p class="text g-text">
          {{ $t("IPA_CACHE_DESC_2") }}
        </p>

        <table class="table">
          <tr>
            <td class="cell">{{ $t("IN_UPDATE") }}</td>
            <td class="td">{{ status.inUpdate ? $t("YES") : $t("NO") }}</td>
          </tr>
          <tr>
            <td class="cell">{{ $t("AMOUNT_OF_APPS_IN_CACHE") }}</td>
            <td class="td">{{ status.total }}</td>
          </tr>
          <tr>
            <td class="cell">{{ $t("CACHE_SIZE") }}</td>
            <td class="td">{{ status.size }}</td>
          </tr>
          <tr>
            <td class="cell">{{ $t("CACHE_SIZE_LIMIT") }}</td>
            <td class="td">{{ status.sizeLimit }}</td>
          </tr>
          <tr v-if="+new Date(status.date)">
            <td class="cell">{{ $t("CACHE_UPDATED") }}</td>
            <td class="td">
              {{ new Date(status.date).toLocaleString() }} ({{
                $t("DAYS_AGO", { count: status.daysAgo })
              }})
            </td>
          </tr>
        </table>

        <div class="controls">
          <btn
            class="controls__btn"
            @click.native="reinstall"
            :loading="reinstalling || status.inUpdate"
            type="primary"
          >
            {{ $t("REINSTALL_EVERYTHING") }}
          </btn>

          <btn
            class="controls__btn"
            @click.native="clear"
            :loading="clearing"
            type="primary"
          >
            {{ $t("CLEAR_IPA_CACHE") }}
          </btn>

          <btn
            class="controls__btn"
            @click.native="revalidate"
            :loading="revalidating"
            type="primary"
          >
            {{ $t("REVALIDATE_IPA_CACHE") }}
          </btn>
        </div>

        <template v-if="status.ipas && status.ipas.length">
          <h2 class="intro">
            {{ $t("APPS_IN_CACHE") }}
          </h2>

          <div v-if="!isMobile" class="table-apps-wrapper">
            <table class="table-apps">
              <thead class="table-apps__head table-apps__row">
                <tr>
                  <th class="table-apps__cell">{{ $t("APP_NAME") }}</th>
                  <th class="table-apps__cell">{{ $t("SIZE") }}</th>
                  <th class="table-apps__cell">{{ $t("CACHING_DATE") }}</th>
                  <th class="table-apps__cell">{{ $t("ACTIONS") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  class="table-apps__row"
                  v-for="app in status.ipas"
                  :key="app.bundle_id"
                >
                  <td class="table-apps__cell">{{ app.name }}</td>
                  <td class="table-apps__cell">{{ app.size_hr }}</td>
                  <td class="table-apps__cell">
                    {{ new Date(app.added_at).toLocaleString() }}
                  </td>
                  <td class="table-apps__cell">
                    <button
                      class="g-button"
                      :class="{
                        'g-button_disabled': removingAppsIds.includes(
                          app.bundle_id
                        ),
                      }"
                      @click.prevent="removeApp(app.bundle_id)"
                    >
                      <loader
                        v-show="removingAppsIds.includes(app.bundle_id)"
                        size="13px"
                        border="3px"
                        absolute
                      />
                      {{ $t("REMOVE") }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="commands">
            <div
              class="command"
              v-for="app in status.ipas"
              :key="app.bundle_id"
            >
              <span class="command__heading">{{ $t("APP_NAME") }}</span>
              <span>{{ app.name }}</span>
              <span class="command__heading">{{ $t("SIZE") }}</span>
              <span>{{ app.size_hr }}</span>
              <span class="command__heading">{{ $t("CACHING_DATE") }}</span>
              <span>{{ new Date(app.added_at).toLocaleString() }}</span>
              <span class="command__heading">{{ $t("ACTIONS") }}</span>
              <button
                class="g-button"
                :class="{
                  'g-button_disabled': removingAppsIds.includes(app.bundle_id),
                }"
                @click.prevent="removeApp(app.bundle_id)"
              >
                <loader
                  v-show="removingAppsIds.includes(app.bundle_id)"
                  size="13px"
                  border="3px"
                  absolute
                />
                {{ $t("REMOVE") }}
              </button>
            </div>
          </div>
        </template>

        <h2 class="intro">
          {{
            $t("OTHER_DEVICES_LINKED_WITH_EMAIL", {
              email: activeDevice.email,
            })
          }}
        </h2>

        <p v-if="!inactiveDevices.length">
          {{
            $t("NO_DEVICES_OF_TYPE", {
              type: activeDevice.type,
            })
          }}
        </p>

        <div v-for="device in inactiveDevices" :key="device.lt">
          <h3 class="h3">
            <b>{{ device.name }}</b> - {{ device.model }} ({{ device.ios }})
          </h3>
          <table class="table">
            <tr>
              <td class="cell">{{ $t("IN_UPDATE") }}</td>
              <td class="td">
                {{ statuses[device.lt].inUpdate ? $t("YES") : $t("NO") }}
              </td>
            </tr>
            <tr>
              <td class="cell">{{ $t("AMOUNT_OF_APPS_IN_CACHE") }}</td>
              <td class="td">{{ statuses[device.lt].total }}</td>
            </tr>
            <tr>
              <td class="cell">{{ $t("CACHE_SIZE") }}</td>
              <td class="td">{{ statuses[device.lt].size }}</td>
            </tr>
            <tr v-if="+new Date(status.date)">
              <td class="cell">{{ $t("CACHE_UPDATED") }}</td>
              <td class="td">
                {{ new Date(statuses[device.lt].date).toLocaleString() }} ({{
                  $t("DAYS_AGO", { count: statuses[device.lt].daysAgo })
                }})
              </td>
            </tr>
          </table>
          <div class="controls">
            <btn
              class="controls__btn"
              @click.native="transfer(device.lt)"
              :loading="statuses[device.lt].transferring"
              type="primary"
            >
              {{ $t("TRANSFER_IPA_CACHE") }}
            </btn>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Btn from '@/components/Button';
import Loader from '@/components/Loader';
import moment from 'moment';
import { mapState } from 'vuex';

export default {
  components: { Btn, Loader },
  data() {
    return {
      isMobile: window.innerWidth <= 767 || window.innerHeight <= 767,
      loading: true,
      reinstalling: false,
      clearing: false,
      revalidating: false,
      statuses: {},
      interval: null,
      removingAppsIds: [],
    };
  },
  computed: {
    ...mapState(['devices', 'lt']),
    title() {
      return this.$t('VIEW_IPA_CACHE_STATUS');
    },
    status() {
      return this.statuses[this.lt] || {};
    },
    activeDevice() {
      return this.devices.find(device => device.lt === this.lt) || {};
    },
    inactiveDevices() {
      return this.devices.filter(device => device.lt !== this.lt
        && (device.type === this.activeDevice.type
        || this.activeDevice.compatibleCacheTypes.includes(device.type)));
    },
  },
  methods: {
    getDaysAgo(date) {
      if (!date) {
        return null;
      }
      const days = moment().diff(date, 'days');
      return days;
    },
    async reinstall() {
      await this.$confirm(this.$t('IPA_CACHE_REINSTALL_WARNING'));

      this.reinstalling = true;
      await this.$api.installFromCache()
        .finally(() => {
          this.reinstalling = false;
        });

      await this.$confirm(this.$t('IPA_CACHE_REINSTALL_STATUS'));

      this.$router.push('/my/status');
    },
    async clear() {
      await this.$confirm(this.$t('IPA_CACHE_CLEAR_CONFIRMATION'));

      this.clearing = true;

      await this.$api.clearIPACache()
        .then(() => this.getStatus())
        .finally(() => {
          this.clearing = false;
        });
    },
    async revalidate() {
      await this.$confirm(this.$t('IPA_CACHE_REVALIDATE_CONFIRMATION'));

      this.revalidating = true;

      await this.$api.revalidateIPACache()
        .finally(() => {
          this.revalidating = false;
        });

      this.$router.push('/my/status');
    },
    async transfer(lt) {
      await this.$confirm(this.$t('IPA_CACHE_TRANSFER_WARNING'));

      this.statuses[lt].transferring = true;

      await this.$api.transferIPACache(lt)
        .then(() => this.getStatus(this.lt))
        .then(() => {
          this.$alert(this.$t('IPA_CACHE_TRANSFER_SUCCESS'));
        })
        .finally(() => {
          this.statuses[lt].transferring = false;
        });
    },
    getStatuses() {
      const promises = [];
      [...this.inactiveDevices, this.activeDevice].forEach((device) => {
        promises.push(this.getStatus(device.lt));
      });
      return Promise.all(promises);
    },
    getStatus(lt) {
      return this.$api.getIPACacheStatus(lt).then((res) => {
        const { data } = res;
        const status = {};
        status.inUpdate = data.in_update;
        status.size = data.size_hr;
        status.sizeLimit = data.size_limit_hr;
        status.date = data.updated_at;
        status.daysAgo = this.getDaysAgo(status.date);
        status.total = res.total;
        status.transferring = false;
        status.ipas = data.ipas;
        this.$set(this.statuses, lt, status);
      });
    },
    removeApp(id) {
      this.removingAppsIds.push(id);
      this.$api.removeAppFromIPACache(id)
        .then(() => this.getStatus(this.lt))
        .finally(() => {
          this.removingAppsIds = this.removingAppsIds.filter(x => x !== id);
        });
    },
  },
  created() {
    this.getStatuses()
      .finally(() => {
        this.loading = false;
      });

    this.interval = setInterval(() => this.getStatus(this.lt), 5 * 1000);
  },
  destroyed() {
    clearInterval(this.interval);
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/variables";

b {
  font-weight: 600;
}

.main {
  display: block;
  flex-grow: 1;
}

.intro {
  &__inner {
    margin-right: 5px;
  }

  &__status {
    font-size: inherit;
    font-weight: 400;
  }

  &:not(:first-child) {
    margin-top: 30px;
    margin-bottom: 20px;
  }
}

.text {
  margin-bottom: 8px;
}

.row {
  display: flex;
  align-items: flex-start;

  @include notebook {
    flex-wrap: wrap;
  }
}

.button {
  font-size: 16px;
}

.h3 {
  margin-top: 20px;
}

.table {
  width: 100%;
  line-height: 20px;
  border: 1px solid #e5e5e5;
  border-left: none;
  border-right: none;
  padding-top: 15px;
  margin-top: 15px;
}

tr:first-child td {
  padding-top: 15px;
}

tr:last-child td {
  padding-bottom: 15px;
}

.td {
  padding-left: 40px;
}

.cell {
  width: 170px;
  flex: 0 0 170px;
}

.controls {
  margin-top: 5px;

  &__btn {
    margin-top: 10px;
  }

  &__btn:not(:last-child) {
    margin-right: 10px;
  }
}

.table-apps-wrapper {
  @include notebook {
    overflow-x: auto;
    width: 100vw;
    margin-left: -8px;
  }
}

.commands {
  margin-top: 15px;
}

.command {
  padding: 13px 0;
  border-bottom: 1px solid #e5e5e5;

  &:first-child {
    border-top: 1px solid #e5e5e5;
  }

  &__heading {
    font-size: 15px;
    font-weight: 500;
    display: block;
    margin-bottom: 5px;

    &:not(:first-child) {
      margin-top: 10px;
    }
  }

  &__date {
    display: block;
    white-space: nowrap;
    color: $text-secondary;
    font-size: 12px;
    margin-bottom: 5px;
  }
}

.table-apps__date,
.command__date {
  display: block;
  white-space: nowrap;
  color: $text-secondary;
  font-size: 12px;
  margin-bottom: 5px;
}

.table-apps__status,
.command__status {
  font-size: 14px;
  white-space: nowrap;
}

.table-apps__button,
.command__button {
  padding-top: 0px;
  padding-bottom: 1px;
  margin-left: 5px;
}

.table-apps {
  margin-top: 15px;
  text-align: left;
  width: 100%;

  &__head {
    font-weight: 500;
  }

  &__cell {
    padding: 8px;

    &_nw {
      white-space: nowrap;
    }
  }

  &__row {
    border-bottom: 1px solid #e5e5e5;

    &:hover {
      background-color: $lighter-primary;
    }
  }
}
</style>
